// The pure helpers every writer shares, ported from Pipeline.swift (`CompiledManuscript`'s derived values,
// `CompilePipeline.heading/header/contentsEntries/numberWords/roman/documentTitle/shortTitle`), Preset.swift
// (`Page.parse/canonical/inches/resolvedMargins`, `publicationEdition`) and `AuthorInfo`.
import { BookMatter, editionFrom, numberWords as coreNumberWords, trimCFWhitespace, type Edition } from '@writing-suite/core';
import { creditLines } from '@writing-suite/core/members';
import type {
  AuthorInfo, CompiledManuscript, CompiledSection, CompilePreset, Page, PenName, SectionRole, TitlePageContent,
} from './types.ts';

// MARK: Numbers as the Mac formats them

/** `Int.formatted()` in English: digit grouping with commas. */
export const formatted = (n: number) => Math.round(n).toLocaleString('en-US');

/** `Double.formatted()` for the validation messages: up to three decimals, grouped. */
export const formattedDouble = (x: number) => x.toLocaleString('en-US', { maximumFractionDigits: 3 });

// MARK: AuthorInfo

const lastWord = (name: string) => {
  const words = name.split(' ').filter((w) => w !== '');
  return words.length ? words[words.length - 1] : name;
};

/** `AuthorInfo.resolvedSurname`. */
export const resolvedSurname = (a: AuthorInfo) => a.surname ?? lastWord(a.name);

/** `AuthorInfo.contactName`: the name heading a contact block or letter. */
export const contactName = (a: AuthorInfo) => (a.legalName !== undefined && trimCFWhitespace(a.legalName) !== '' ? a.legalName : a.name);

export const isPenName = (a: AuthorInfo) => contactName(a) !== a.name;
/** `AuthorInfo.withholdsOwnName`: a pen name whose own contact details stand in for the writer's. */
export const withholdsOwnName = (a: AuthorInfo) => a.ownNameWithheld === true;

/** Every published name, the project's own first, then the co-authors (W9c). */
export const authorNames = (a: AuthorInfo): string[] =>
  [a.name, ...(a.coAuthors ?? []).map(trimCFWhitespace).filter((n) => n !== '' && n !== a.name)];

/** `AuthorInfo.credits`: a musical's title-page credits ("Book by A", "Music and Lyrics by B"), null when nobody has a
 *  role. */
export function authorCredits(a: AuthorInfo): string[] | null {
  return creditLines({ names: authorNames(a).slice(1), joiner: a.joiner ?? 'and', roles: a.roles ?? {} }, a.name);
}

/** The byline: "A", "A and B", "A, B and C" (the joiner before the last name, no serial comma). */
export function bylineName(a: AuthorInfo): string {
  const names = authorNames(a);
  if (names.length < 2) return a.name;
  const joiner = trimCFWhitespace(a.joiner ?? '') || 'and';
  return `${names.slice(0, -1).join(', ')} ${joiner} ${names[names.length - 1]}`;
}

/** `{surname}`: the author's, or every author's joined " / " (Shunn's header for co-authors: "Ng / Okafor / Title / 3"). */
export const headerSurnames = (a: AuthorInfo): string =>
  [resolvedSurname(a), ...authorNames(a).slice(1).map(lastWord)].join(' / ');

/** `PenName.resolvedSurname`. */
export const penNameSurname = (p: PenName) =>
  p.surname !== undefined && trimCFWhitespace(p.surname) !== '' ? p.surname : lastWord(p.name);

/** `AuthorInfo.publishing(name:surname:contact:penName:)`: the writer's details under a pen name (or their own); a pen
 *  name with contact details of its own gives those under the pen name, and leaves the writer's out. */
export function publishingAuthor(o: { name: string; surname?: string; contact?: string[]; penName?: PenName | null }): AuthorInfo | null {
  const own = trimCFWhitespace(o.name);
  const contact = o.contact ?? [];
  if (o.penName && trimCFWhitespace(o.penName.name) !== '') {
    if (o.penName.contact !== undefined) {
      const its = o.penName.contact.map(trimCFWhitespace).filter((l) => l !== '');
      return { name: trimCFWhitespace(o.penName.name), surname: penNameSurname(o.penName), contact: its, ownNameWithheld: true };
    }
    return { name: trimCFWhitespace(o.penName.name), surname: penNameSurname(o.penName), contact, ...(own === '' ? {} : { legalName: own }) };
  }
  return own === '' ? null : { name: own, ...(o.surname !== undefined ? { surname: o.surname } : {}), contact };
}

// MARK: Page sizes

export const NAMED_PAGES: Record<string, [number, number]> = { letter: [8.5, 11], a4: [8.27, 11.69], a5: [5.83, 8.27] };

/** Swift's `Double(_ text:)`: a decimal or hex float literal, `inf`/`nan`; nil for anything else. */
function swiftDouble(text: string): number | null {
  const t = text;
  if (/^[+-]?(\d+\.?\d*([eE][+-]?\d+)?|\.\d+([eE][+-]?\d+)?)$/.test(t)) return Number(t);
  if (/^[+-]?(inf|infinity)$/i.test(t)) return t.startsWith('-') ? -Infinity : Infinity;
  if (/^[+-]?nan$/i.test(t)) return NaN;
  return null;
}

/** `Page.parse`: a named size, or inches as "6x9", "6 x 9" or "6 × 9"; null when it isn't a plausible page. */
export function parsePageSize(size: string): [number, number] | null {
  const key = size.trim().toLowerCase();
  if (NAMED_PAGES[key]) return NAMED_PAGES[key];
  const parts = key.replaceAll('×', 'x').split('x').map((p) => swiftDouble(trimCFWhitespace(p)));
  if (parts.length !== 2) return null;
  const [w, h] = parts;
  if (w === null || h === null || ![w, h].every((v) => Number.isFinite(v) && v >= 1 && v <= 60)) return null;
  return [w, h];
}

/** `Page.canonical`: a name in lowercase, else "6x9". */
export function canonicalPageSize(size: string): string | null {
  const key = size.trim().toLowerCase();
  if (NAMED_PAGES[key]) return key;
  const parsed = parsePageSize(key);
  if (!parsed) return null;
  const number = (x: number) => (Number.isInteger(x) ? String(x) : String(x));
  return `${number(parsed[0])}x${number(parsed[1])}`;
}

/** `Page.inches(of:)`: Letter when unreadable (presets are validated when read). */
export function pageInches(size: string): { width: number; height: number } {
  const p = parsePageSize(size) ?? [8.5, 11];
  return { width: p[0], height: p[1] };
}

/** `Page.resolvedMargins`. */
export function resolvedMargins(page: Page): { inside: number; outside: number; top: number; bottom: number } {
  return { inside: page.inside ?? page.margins, outside: page.outside ?? page.margins, top: page.top ?? page.margins, bottom: page.bottom ?? page.margins };
}

/** `CompilePreset.publicationEdition`. */
export function publicationEdition(preset: CompilePreset): Edition | null {
  if (preset.edition !== undefined) return editionFrom(preset.edition);
  switch (preset.format) {
    case 'epub': return 'ebook';
    case 'audiobook': return 'audiobook';
    case 'pdf': return preset.render.print ? 'paperback' : null;
    default: return null;
  }
}

// MARK: CompiledManuscript's derived values

/** A single Submission document rather than the book. */
export const isDocument = (m: CompiledManuscript) => m.preset.select.document !== undefined;
/** One journal post rather than the whole journal. */
export const isPost = (m: CompiledManuscript) => m.preset.select.onePost === true;
/** A title page written in the Front Matter folder replaces the preset's own. */
export const hasTitlePageMatter = (m: CompiledManuscript) => m.sections.some((s) => s.matter === 'title-page');

/** `wordCountPhrase`: "about 79,300 words", per the preset's rounding. */
export function wordCountPhrase(m: CompiledManuscript): string {
  const rounding = m.preset.render.titlePage.wordCountRounding;
  if (rounding <= 0) return `${formatted(m.wordCount)} words`;
  const rounded = Math.max(rounding, swiftRound(m.wordCount / rounding) * rounding);
  return `about ${formatted(rounded)} words`;
}

/** Swift's `rounded()`: half away from zero. */
export const swiftRound = (x: number) => (x < 0 ? -Math.round(-x) : Math.round(x));

/** `titlePageContent`: what the preset's own title page shows; null when there is none. */
export function titlePageContent(m: CompiledManuscript): TitlePageContent | null {
  const tp = m.preset.render.titlePage;
  if (!tp.include || hasTitlePageMatter(m)) return null;
  return {
    contact: tp.contact && m.author ? [contactName(m.author), ...m.author.contact] : [],
    wordCount: tp.wordCount ? wordCountPhrase(m) : null,
    title: m.title,
    byline: tp.byline && m.author ? `by ${bylineName(m.author)}` : null,
  };
}

// MARK: Headings

export const numberWords = coreNumberWords;

export function roman(n: number): string {
  const table: [number, string][] = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
    [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
  let out = '';
  for (const [value, numeral] of table) while (n >= value) { out += numeral; n -= value; }
  return out;
}

/** `CompilePipeline.heading`: a part or chapter heading's lines, filled; a `{title}` line without a title is dropped. */
const NUMBER_PLACEHOLDERS = ['{number}', '{numberWords}', '{roman}'];

/** The heading's lines with the placeholders filled. A line whose `{title}` is empty is left out; so is a line naming
 *  a number on an unnumbered section (a prologue) — and if that leaves nothing, the title alone. */
export function heading(lines: string[], section: Pick<CompiledSection, 'number' | 'title'> & { numbered?: boolean }): string[] {
  const numbered = section.numbered ?? true;
  const filled = lines.flatMap((template) => {
    if (template.includes('{title}') && section.title === '') return [];
    if (!numbered && NUMBER_PLACEHOLDERS.some((p) => template.includes(p))) return [];
    const line = template
      .replaceAll('{numberWords}', numberWords(section.number))
      .replaceAll('{number}', String(section.number))
      .replaceAll('{roman}', roman(section.number))
      .replaceAll('{title}', section.title);
    return trimCFWhitespace(line) === '' ? [] : [line];
  });
  if (!filled.length && !numbered && section.title !== '') return [section.title];
  return filled;
}

/** `CompilePipeline.header`: header placeholders filled; `{page}` is left for the renderer. */
export function header(template: string, m: CompiledManuscript): string {
  return template
    .replaceAll('{surname}', m.author ? headerSurnames(m.author) : '')
    .replaceAll('{author}', m.author ? bylineName(m.author) : '')
    .replaceAll('{shortTitle}', m.shortTitle)
    .replaceAll('{title}', m.title)
    .replaceAll(' /  / ', ' / ');
}

/** `TypstWriter.bookPageKinds`: matter laid out as book pages (never listed in Contents). */
export const BOOK_PAGE_KINDS: ReadonlySet<BookMatter> = new Set<BookMatter>(['half-title', 'title-page', 'copyright', 'dedication', 'epigraph', 'contents']);

/** `isListedInContents`: parts, chapters, and back matter with a heading. */
export function isListedInContents(role: SectionRole, matter: BookMatter | null): boolean {
  switch (role) {
    case 'part': case 'chapter': return true;
    case 'back-matter': return matter ? !BOOK_PAGE_KINDS.has(matter) : true;
    default: return false;
  }
}

/** `contentsEntries`: each listed section and its line. */
export function contentsEntries(m: CompiledManuscript): { section: CompiledSection; text: string }[] {
  const r = m.preset.render;
  return m.sections.flatMap((s) => {
    if (!isListedInContents(s.role, s.matter)) return [];
    switch (s.role) {
      case 'part': return [{ section: s, text: heading(r.partHeading, s).join(': ') }];
      case 'chapter': return [{ section: s, text: heading(r.chapterHeading, s).join(' · ') }];
      default: return [{ section: s, text: s.title }];
    }
  });
}

/** `roundedWords`: to the thousand ("79,000"), or the hundred when short. */
export function roundedWords(words: number): string {
  const step = words >= 10_000 ? 1000 : 100;
  return formatted(swiftRound(words / step) * step);
}

/** `documentTitle`: a single-document compile's title lines, filled; lines naming a missing author are dropped.
 *  `year`: the Swift uses today's year (`Date()`). */
export function documentTitle(m: CompiledManuscript, year = new Date().getFullYear()): string[] {
  return (m.preset.render.documentTitle ?? []).flatMap((line) => {
    if (line.includes('{author}') && m.author === null) return [];
    const words = m.manuscriptWordCount !== null ? roundedWords(m.manuscriptWordCount) : '';
    return [BookMatter.fillBook(line, { title: m.title, author: m.author ? bylineName(m.author) : null, shortTitle: m.shortTitle, year, words })];
  });
}

/** `shortTitle`: the title up to any colon, without a leading article, in capitals. */
export function shortTitle(title: string): string {
  const pieces = title.split(':').filter((p) => p !== '');
  let t = pieces.length ? pieces[0] : title;
  for (const article of ['The ', 'A ', 'An ']) {
    if (t.startsWith(article) && t.split(' ').filter((w) => w !== '').length > 2) t = t.slice(article.length);
  }
  return t.toUpperCase();
}
